import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getContractListThunk, getContractTypesListThunk } from "tm-shared/api";
import { Contract } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { getPointContractSourceIds, usePoint } from "@/domains/point";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";

import { StyledAPContainer } from "../../Styled";
import {
    getInfoTabContractsBlocks,
    IEnrichedContract,
    InfoTabContractsModel,
    isContractExpired,
} from "./InfoTabContracts.blocks";
import { getInfoTabContractsCapabilities } from "./InfoTabContracts.policy";

const InfoTabContracts = () => {
    const { pointId = "" } = useParams<{ pointId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);

    const role = useAppSelector((state) => state.user.role);
    const targetsContracts = useAppSelector(
        (state) => state.offline.targetsContracts,
    );
    const contractTypesList = useAppSelector(
        (state) => state.offline.contractTypesList,
    );

    const { point, pointLoading } = usePoint(pointId);

    const capabilities = useMemo(
        () => getInfoTabContractsCapabilities(role),
        [role],
    );

    const allContracts = useMemo(() => {
        if (!point || !targetsContracts) return [];

        const contractSources = getPointContractSourceIds(point);

        const contracts: Contract[] = [];
        const seenIds = new Set<string>();

        contractSources.forEach((sourceId) => {
            const sourceContracts = targetsContracts[sourceId!];
            if (sourceContracts) {
                Object.values(sourceContracts).forEach((contract) => {
                    if (contract && !seenIds.has(contract.id)) {
                        contracts.push(contract);
                        seenIds.add(contract.id);
                    }
                });
            }
        });

        return contracts;
    }, [point, targetsContracts]);

    const enrichedContracts = useMemo<IEnrichedContract[]>(
        () =>
            allContracts.map((contract) => ({
                ...contract,
                isExpired: isContractExpired(contract.endDate),
            })),
        [allContracts],
    );

    useEffect(() => {
        if (!pointId) return;

        const loadContracts = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    dispatch(
                        getContractListThunk({
                            pointId: pointId,
                            isActive: capabilities.onlyActiveContracts,
                        }),
                    ),
                    dispatch(getContractTypesListThunk()),
                ]);
            } catch (error) {
                console.error("Error loading contracts:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadContracts();
    }, [pointId, capabilities.onlyActiveContracts, dispatch]);

    const handleAddContract = useCallback(() => {
        navigate(buildRoutePath(ROUTES.PointAddContract, { pointId }));
    }, [navigate, pointId]);

    const model: InfoTabContractsModel = useMemo(
        () => ({
            capabilities,
            pointId,
            enrichedContracts,
            contractTypesList,
            onAddContract: handleAddContract,
        }),
        [
            capabilities,
            pointId,
            enrichedContracts,
            contractTypesList,
            handleAddContract,
        ],
    );

    const blocks = useMemo(() => getInfoTabContractsBlocks(model), [model]);

    if (isLoading || pointLoading) {
        return (
            <>
                <Meta title="TT Info Contracts" />
                <Header title="Контракти" back titleFontSize={16} />
                <StyledAPContainer>
                    <Loading />
                </StyledAPContainer>
            </>
        );
    }

    return (
        <>
            <Meta title="TT Info Contracts" />
            <Header title="Контракти" back titleFontSize={16} />

            <StyledAPContainer>
                <BlockList blocks={blocks} model={model} />
            </StyledAPContainer>
        </>
    );
};

export default InfoTabContracts;
